import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Relation } from '@tcc-mono/vandijk/shared/data-access/models';
import { RelationService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { RelationRepository } from '@tcc-mono/vandijk/web/crm/data-access';
import { Observable, combineLatest, filter, forkJoin, map, of, switchMap, take, tap, zip } from 'rxjs';

@Component({
  selector: 'tcc-mono-display-relation-property',
  template: `
  <ng-container *subscribe="relationProp$ as prop;">
    <span *skeleton="!prop; height: '100%'; width: '250px'">
      {{ prop }}
    </span>
  </ng-container>
`,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisplayRelationPropertyComponent implements OnInit, OnChanges {
  private readonly _repo: RelationRepository = inject(RelationRepository);

  @Input() public relationId: number;
  @Input() public property: string = 'name';
  @Input() public displayedValueWhenNoMatch: string = '-'

  public relationProp$: Observable<unknown>;

  public ngOnInit(): void {
    this.initProp();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['relationId'] || changes['property']) && !(
      changes['relationId'].previousValue === changes['relationId'].currentValue
      && changes['property'].previousValue === changes['property'].currentValue
    )) {
      this.initProp();
    }
  }

  public initProp(): void {
    if (this.property === 'emailsSentTo') {
      this.relationProp$ = this._repo.selectRelation(this.relationId)
        .pipe(
          filter((relation) => !!relation),
          tap((relation) => {
            if (relation.invoice_relation_id) {
              this._repo.getRelation(+relation.invoice_relation_id, false).pipe(take(1)).subscribe();
            }
          }),
          switchMap((relation) => combineLatest([
            of(relation),
            relation.invoice_relation_id ? this._repo.selectRelation(+relation.invoice_relation_id) : of(relation)
          ])),
          map(([relation, invoiceRelation]) => {
            return invoiceRelation?.invoice_email
              || invoiceRelation?.postal_email
              || invoiceRelation?.visit_email
              || relation?.invoice_email
              || relation?.postal_email
              || relation?.visit_email
              || '-'
          })
        );
    } else if (this.relationId) {
      this.relationProp$ = this._repo.selectRelation(this.relationId)
        .pipe(
          map((relation) => (relation)?.[this.property]),
        );

      this._repo.getRelation(this.relationId, false).pipe(take(1)).subscribe();
    } else {
      this.relationProp$ = of(this.displayedValueWhenNoMatch);
    }
  }
}
